import { useContext, type FC } from "react";
import type { MetaFunction, LinksFunction } from "@remix-run/node";
import {
  Box,
  Container,
  Divider,
  HStack,
  Heading,
  Image,
  Text,
} from "@chakra-ui/react";
import FeaturedBook from "../components/FeaturedBook";
import { ShopSection } from "~/components/ShopSection";
import { AboutUsSection } from "~/components/AboutUsSection";
import ContactForm from "../components/ContactForm";
import { BaseLayout } from "~/layouts/main/MainLayout";
import { CiteSection } from "~/components/CiteSection";
import { bookPageData } from "~/data-store.server";
import { useLoaderData } from "@remix-run/react";
import { DataContext, DataProvider } from "~/context/product-data.context";
import type { DataContextType } from "~/context/product-data.context";
import { ProductBookAd } from "~/components/ProductBookAd";
import { metaOg } from "~/utils/meta";
import backgroundImgLG from "~/assets/background_lg.webp";
import backgroundImgMD from "~/assets/background_md.webp";
import { bookStructuredData } from "~/utils/book-structured-data";
import type { HandleStructuredData } from "~/utils/structuredData";

type HandleType = HandleStructuredData<Awaited<ReturnType<typeof loader>>>;

export async function loader() {
  const books = await bookPageData();
  return { bookPageData: books };
}

export const handle: HandleType = {
  structuredData(data) {
    const book = data.bookPageData.find((b) => b.id === "1");
    return book ? bookStructuredData(book) : null;
  },
};

export const meta: MetaFunction = () => {
  return [
    { title: "Wydawnictwo Dystans - Literatura sportowa" },
    {
      name: "keywords",
      content:
        "Wydawnictwo dystans, kolarz, tim krabbe, kolarstwo, książka, literatura sportowa",
    },
    {
      name: "description",
      content:
        "Poznaj inspirujące historie o sporcie i kolarstwie! Nasze wydawnictwo łączy pasję do ruchu z literaturą, publikując wyjątkowe książki, które zachęcają do aktywności i zgłębiania hobby. Odkryj opowieści, które motywują do działania i pokazują sport z różnych perspektyw.",
    },
    ...metaOg({
      pageType: "website",
      siteName: "Wydawnictwo Dystans",
      title: "Wydawnictwo Dystans",
      description:
        "Poznaj inspirujące historie o sporcie i kolarstwie! Nasze wydawnictwo łączy pasję do ruchu z literaturą, publikując wyjątkowe książki, które zachęcają do aktywności i zgłębiania hobby. Odkryj opowieści, które motywują do działania i pokazują sport z różnych perspektyw.",
      image: "https://dystans.cc/images/dystans_sygnet_orange.png",
      url: "https://dystans.cc",
    }),
  ];
};

export const links: LinksFunction = () => {
  return [
    {
      rel: "canonical",
      href: "https://dystans.cc",
    },
  ];
};

type TSectionHeader = {
  title: string;
  subTitle?: string;
};

const SectionHeader: FC<TSectionHeader> = ({ title, subTitle }) => {
  return (
    <Box my={8}>
      <HStack>
        <Divider width={8} bgColor={"blackAlpha.300"} borderWidth={1} />
        <Heading
          px={4}
          as="h2"
          fontSize={{ base: "xl", md: "3xl" }}
          my={2}
          zIndex={10}
          position={"relative"}
          whiteSpace={"nowrap"}
        >
          {title}
        </Heading>
        <Divider bgColor={"blackAlpha.300"} borderWidth={1} />
      </HStack>
      {subTitle && (
        <Text pl={4} size={"xs"}>
          {subTitle}
        </Text>
      )}
    </Box>
  );
};

const SECTION_MARGIN = 16;

const IndexContent = () => {
  const { data } =
    useContext<DataContextType<Awaited<ReturnType<typeof loader>>>>(
      DataContext
    );
  const adProduct = data?.bookPageData.find((item) => item.id === "1");

  return (
    <>
      <Box as="section">
        <FeaturedBook priority="high" />
      </Box>
      <Box as="section" my={SECTION_MARGIN}>
        {adProduct && <ProductBookAd product={adProduct} />}
      </Box>
      <Divider />
      <Box as="section" my={SECTION_MARGIN}>
        <CiteSection />
      </Box>
      <Box as="section" height="300px" my={SECTION_MARGIN}>
        <Box
          height="100%"
          width="100vw"
          position="relative"
          left={"50%"}
          ml={"-50vw"}
          maxWidth={"100vw"}
          zIndex={-1}
        >
          <Image
            loading={"lazy"}
            fetchPriority={"low"}
            objectPosition={"50% 70%"}
            objectFit={"cover"}
            src={backgroundImgLG}
            srcSet={`${backgroundImgMD} 768w, ${backgroundImgLG} 992w`}
            alt="Kolarz na przełęczy"
            width={"100%"}
            height={"100%"}
          />
          <Container maxW="1024px">
            <Box
              position={"absolute"}
              margin={"auto"}
              top={"50%"}
              transform={"translateY(-50%)"}
            >
              <Divider
                w="70px"
                h="6px"
                bgColor="primary.500"
                border={0}
                mb={4}
                opacity={1}
              />
              <Text
                fontSize={"3xl"}
                fontWeight={"regular"}
                textColor="white"
                lineHeight={1.5}
              >
                kilometry
              </Text>
              <Text
                fontSize={"3xl"}
                fontWeight={"regular"}
                textColor="white"
                lineHeight={1.5}
              >
                sportowych
              </Text>
              <Text
                fontSize={"3xl"}
                fontWeight={"regular"}
                textColor="primary.500"
                lineHeight={1.5}
              >
                inspiracji
              </Text>
            </Box>
          </Container>
        </Box>
      </Box>
      <Box as="section" id="sklep" my={SECTION_MARGIN} scrollMarginTop={100}>
        <SectionHeader
          title="Sklep"
          subTitle="Zapraszamy do zakupu naszych wydawniczych pozycji."
        />
        {data && (
          <ShopSection productsList={data.bookPageData} priority={"low"} />
        )}
      </Box>
      <Box id="o-nas" as="section" my={SECTION_MARGIN} scrollMarginTop={100}>
        <SectionHeader title="O nas" />
        <AboutUsSection priority={"low"} />
      </Box>
      <Box id="kontakt" as="section" my={SECTION_MARGIN} scrollMarginTop={100}>
        <SectionHeader
          title="Kontakt"
          subTitle="W razie jakichkolwiek pytań / problemów zapraszamy do kontaktu telefonicznie lub poprzez poniższy formularz."
        />
        <ContactForm />
      </Box>
    </>
  );
};

export default function Index() {
  const pageData = useLoaderData<typeof loader>();

  return (
    <DataProvider initialData={pageData}>
      <BaseLayout>
        <IndexContent />
      </BaseLayout>
    </DataProvider>
  );
}
